import { useEffect, useState, useMemo } from "react"
import PageHeader from "../../components/PageHeader/PageHeader"
import { ROOT, TRANSACTIONS } from "../../constants/path"
import { GridParent } from "../BankAccounts/styles"
import Table from "../../components/Table/Table"
import { GridColDef, GridRowsProp, GridSortModel } from "@mui/x-data-grid"
import { useAppDispatch, useAppSelector } from "../../app/hooks"
import {
  fetchBankStatement,
  fetchUploadHistory,
  resetFilters,
  selectUploadHistory,
  setFilterCount,
  setFilters,
  setFiltersFromURL,
  setPagination,
  uploadHistoryInitialState,
} from "../../slices/uploadHistorySlice"
import { getBankList, PaginationType } from "../../slices/transactionsSlice"
import {
  ContentWrapper,
  StyledButton,
  StyledCircle,
  StyledFilterDrawer,
  TransactionsContainer,
} from "../Transactions/styles"
import { StatusChip } from "./StatusChip"
import dayjs from "dayjs"
import { INDIAN_STANDARD_DATE_TIME } from "../../constants/format"
import { Box, Button } from "@mui/material"
import CloseIcon from "@mui/icons-material/Close"
import { FilterAltRounded } from "@mui/icons-material"
import UploadHistoryFilters from "./UploadHistoryFilter"
import queryString from "query-string"
import { useNavigate } from "react-router-dom"

const UploadHistory = () => {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const [rowCountState, setRowCountState] = useState(0)
  const [filterOpen, setFilterOpen] = useState(false)
  const drawerWidth = 280
  const { uploadHistory, loading, pageInfo, pagination, filters, filterCount } =
    useAppSelector(selectUploadHistory)

  const fetchUpload = (
    currentFilters = filters,
    currentPagination = pagination,
  ) => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { page, pageSize, ...cleanFilters } = currentFilters as any
    const params = queryString.stringify({
      ...cleanFilters,
      ...currentPagination,
    })
    navigate({ search: params }, { replace: true })
    dispatch(fetchUploadHistory({ ...currentPagination, ...currentFilters }))
    dispatch(setFilterCount())
  }

  useEffect(() => {
    dispatch(getBankList())
    // URL Parameters to Redux State
    const params = queryString.parse(location.search)

    let initialFilters = { ...filters }
    const initialPagination = { ...pagination }

    if (Object.keys(params).length !== 0) {
      // URL has filter parameters
      if (!Array.isArray(params.page) && !Array.isArray(params.pageSize)) {
        initialPagination.page = parseInt((params.page as string) || "0")
        initialPagination.pageSize = parseInt(
          (params.pageSize as string) || "10",
        )
        dispatch(setPagination(initialPagination))
      }

      const updatedFilters = {
        ...filters,
        ...params,
        bankAccountId: params.bankAccountId
          ? typeof params.bankAccountId === "string"
            ? [params.bankAccountId]
            : ((params.bankAccountId as (string | null)[]).filter(
                Boolean,
              ) as string[])
          : [],
      } as typeof filters
      delete (updatedFilters as any).page
      delete (updatedFilters as any).pageSize
      initialFilters = updatedFilters
      dispatch(setFiltersFromURL(updatedFilters))
    }

    fetchUpload(initialFilters, initialPagination)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    setRowCountState((prevRowCountState: number) =>
      pageInfo?.totalRowCount !== undefined
        ? pageInfo?.totalRowCount
        : prevRowCountState,
    )
  }, [pageInfo?.totalRowCount, setRowCountState])

  useEffect(() => {
    fetchUpload()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.sortBy, filters.orderBy])

  const handleFileDownload = (id: string, name: string) => {
    dispatch(
      fetchBankStatement({
        id,
        name,
      }),
    )
  }

  const columns: GridColDef[] = [
    {
      field: "bankAccount",
      headerName: "Bank Account",
      minWidth: 220,
      flex: 1,
      sortable: false,
    },
    {
      field: "uploadedBy",
      headerName: "Uploaded By",
      minWidth: 150,
      flex: 1,
      sortable: false,
    },
    {
      field: "uploaded_at",
      headerName: "Upload Time",
      minWidth: 180,
      flex: 1,
      sortable: true,
    },
    {
      field: "fileName",
      headerName: "File Name",
      minWidth: 300,
      flex: 1,
      sortable: false,
      renderCell: ({ value, row }) => {
        return value ? (
          <div
            style={{
              color: "#2563eb",
              textDecoration: "underline",
              cursor: "pointer",
            }}
            onClick={(e) => {
              e.preventDefault()
              const name = `${row.bankAccountName}-${row.uploadTime}.${
                value?.split(".").pop() || "csv"
              }`
              handleFileDownload(row.id, name)
            }}
          >
            {value}
          </div>
        ) : (
          "-"
        )
      },
    },
    {
      field: "status",
      headerName: "Status",
      minWidth: 130,
      flex: 1,
      sortable: false,
      renderCell: ({ row }) => {
        return <StatusChip row={row} />
      },
    },
    {
      field: "totalTransactions",
      headerName: "Total Transactions",
      minWidth: 150,
      flex: 1,
      sortable: true,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "uniqueTransactions",
      headerName: "Unique Transactions",
      minWidth: 160,
      flex: 1,
      sortable: true,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "firstTransactionDate",
      headerName: "First Transaction Date",
      minWidth: 180,
      flex: 1,
      sortable: true,
    },
    {
      field: "lastTransactionDate",
      headerName: "Last Transaction Date",
      minWidth: 180,
      flex: 1,
      sortable: true,
    },
  ]
  const rows: GridRowsProp = uploadHistory?.map((upload, index) => ({
    rowIndex: index,
    id: upload.id,
    bankAccountName: upload.accountName,
    bankAccount: upload.accountName + " - ****" + upload.accountNumber,
    uploadedBy: upload.uploadedBy || "-",
    uploaded_at:
      dayjs(upload.uploadedAt).format(INDIAN_STANDARD_DATE_TIME) || "-",
    fileName: upload.filePath.split("/").pop(),
    status: upload.status,
    totalTransactions: upload.totalRecords || 0,
    uniqueTransactions: upload.uniqueRecords || 0,
    firstTransactionDate: upload.firstTransactionDate
      ? dayjs(upload.firstTransactionDate).format(INDIAN_STANDARD_DATE_TIME)
      : "-",
    lastTransactionDate: upload.lastTransactionDate
      ? dayjs(upload.lastTransactionDate).format(INDIAN_STANDARD_DATE_TIME)
      : "-",
  }))

  const setPaginationModel = (page: PaginationType) => {
    dispatch(setPagination(page))
    fetchUpload(filters, page)
  }

  const sortModel = useMemo<GridSortModel>(() => {
    if (!filters.sortBy || filters.sortBy === "none") return []
    return [
      {
        field: filters.sortBy,
        sort: (filters.orderBy as "asc" | "desc") || "desc",
      },
    ]
  }, [filters.sortBy, filters.orderBy])

  const handleSortModelChange = (model: GridSortModel) => {
    if (!model.length) {
      dispatch(setFilters({ ...filters, sortBy: "none" }))
    } else {
      dispatch(
        setFilters({
          ...filters,
          sortBy: model[0].field,
          orderBy: model[0].sort,
        }),
      )
    }
  }

  const columnVisibilityModel = {
    id: false,
  }

  const handleDrawerClose = () => {
    setFilterOpen(false)
  }

  const resetFilterHandler = () => {
    dispatch(resetFilters())
    setFilterOpen(false)
    fetchUpload(
      uploadHistoryInitialState.filters,
      uploadHistoryInitialState.pagination,
    )
  }

  const ApplyFilterHandler = () => {
    setFilterOpen(false)
    fetchUpload()
  }

  return (
    <GridParent>
      <PageHeader
        title="Upload History"
        breadcrums={[
          { title: "Home", path: ROOT },
          { title: "Transactions", path: TRANSACTIONS },
          { title: "Upload History" },
        ]}
        actions={
          <StyledButton
            variant="outlined"
            startIcon={<FilterAltRounded fontSize="small" />}
            endIcon={
              Boolean(filterCount) && <StyledCircle>{filterCount}</StyledCircle>
            }
            color="inherit"
            onClick={() => setFilterOpen(!filterOpen)}
            sx={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              minWidth: "100px",
            }}
          >
            Filters
          </StyledButton>
        }
      />
      <TransactionsContainer>
        <ContentWrapper isOpen={filterOpen} drawerWidth={drawerWidth}>
          <Table
            rows={rows}
            columns={columns}
            loading={loading}
            rowCount={rowCountState}
            pageSizeOptions={[10, 25, 50, 100]}
            paginationModel={pagination}
            paginationMode="server"
            onPaginationModelChange={setPaginationModel}
            sortModel={sortModel}
            onSortModelChange={handleSortModelChange}
            disableColumnFilter
            columnVisibilityModel={columnVisibilityModel}
            disableRowSelectionOnClick
          />
        </ContentWrapper>
        <StyledFilterDrawer
          variant={filterOpen ? "persistent" : "temporary"}
          anchor="right"
          open={filterOpen}
          sx={{
            flexShrink: 0,
          }}
        >
          <Box sx={{ flexGrow: 1, p: 2 }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                mb: 2,
              }}
            >
              <Button color="inherit" onClick={handleDrawerClose}>
                <CloseIcon />
              </Button>
            </Box>
            <UploadHistoryFilters
              resetFilterHandler={resetFilterHandler}
              ApplyFilterHandler={ApplyFilterHandler}
            />
          </Box>
        </StyledFilterDrawer>
      </TransactionsContainer>
    </GridParent>
  )
}

export default UploadHistory
